import React, { useEffect, useRef, useState } from 'react';
import { ArrowDownIcon } from './ui';
import Picture from './Picture';
import { isPrerendering } from '../prerender';

interface Quote {
  text: string;
  author: string;
}

const QUOTES: Quote[] = [
  { text: 'Sobald du den Flug gekostet hast, wirst du für immer auf der Erde wandeln mit den Augen zum Himmel gerichtet, denn dort warst du, und dorthin wirst du immer zurückkehren wollen.', author: 'Leonardo da Vinci' },
  { text: 'Das Fliegen ist der Beweis, dass wir mit Willenskraft das Unmögliche erreichen können.', author: 'Eddie Rickenbacker' },
  { text: 'Die Luft dort oben in den Wolken ist rein und fein, erfrischend und köstlich. Und warum sollte sie das nicht sein? Es ist dieselbe, die die Engel atmen.', author: 'Mark Twain' },
  { text: 'Manchmal fühlt sich das Fliegen zu göttlich an, um von Menschen erreicht zu werden. Manchmal scheint die Welt von oben zu schön, zu wunderbar, zu fern für menschliche Augen.', author: 'Charles A. Lindbergh' },
  { text: 'Der Himmel ist nicht die Grenze, er ist das Zuhause für diejenigen, die die Luftfahrt lieben.', author: 'Anonym' },
];

// Beim Hydrieren das vorgerenderte Zitat übernehmen (sonst Hydration-Mismatch), ansonsten zufällig wählen
const pickInitialQuote = () => {
  const rendered = document.querySelector<HTMLElement>('[data-quote-index]');
  const index = rendered ? Number(rendered.dataset.quoteIndex) : NaN;
  return Number.isInteger(index) && QUOTES[index] ? index : Math.floor(Math.random() * QUOTES.length);
};

interface VideoSource {
  src: string;
  type: string;
}

// Aus media-source/Timeline 1.mp4 (AV1, 12,5 MB) neu kodiert: VP9 für die meisten Browser, H.264 für Safari/iOS
const HERO_VIDEOS: Record<'small' | 'large', VideoSource[]> = {
  small: [
    { src: '/videos/hero-960.webm', type: 'video/webm' },
    { src: '/videos/hero-960.mp4', type: 'video/mp4' },
  ],
  large: [
    { src: '/videos/hero-1280.webm', type: 'video/webm' },
    { src: '/videos/hero-1280.mp4', type: 'video/mp4' },
  ],
};

// Kein Video beim Vorrendern, bei reduzierter Bewegung oder im Datensparmodus – dann bleibt das Standbild
const shouldPlayVideo = () => {
  if (isPrerendering()) return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return !connection?.saveData;
};

// Das Standbild ist das LCP-Element; das Video kommt erst nach dem Laden der Seite dazu und blendet ein, sobald es läuft
const useHeroVideo = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [sources, setSources] = useState<VideoSource[] | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!shouldPlayVideo()) return;
    const start = () => setSources(window.innerWidth < 768 ? HERO_VIDEOS.small : HERO_VIDEOS.large);
    if (document.readyState === 'complete') {
      start();
      return;
    }
    window.addEventListener('load', start, { once: true });
    return () => window.removeEventListener('load', start);
  }, []);

  // Außerhalb des sichtbaren Bereichs pausieren
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        video.play().catch(() => undefined);
      } else {
        video.pause();
      }
    });
    observer.observe(video);
    return () => observer.disconnect();
  }, [sources]);

  return { videoRef, sources, isPlaying, onPlaying: () => setIsPlaying(true) };
};

const ctaBase =
  'items-center bg-blue-600 font-semibold text-white transition-colors hover:bg-blue-700 hover:text-white';

const Hero: React.FC = () => {
  const [quoteIndex] = useState(pickInitialQuote);
  const quote = QUOTES[quoteIndex];
  const { videoRef, sources, isPlaying, onPlaying } = useHeroVideo();

  return (
    <section className="relative h-[620px] overflow-hidden bg-gray-900 text-white lg:h-[720px]">
      <Picture
        src="/images/Flugsportverein_Bilder_1.jpg"
        alt="Segelflugzeug über der Thüringer Landschaft mit Feldern und Windrädern"
        sizes="(max-width: 1023px) 1100px, 100vw"
        priority
        className="absolute inset-0 h-full w-full object-cover object-[center_40%] lg:object-[center_45%]"
      />
      {sources && (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          tabIndex={-1}
          onPlaying={onPlaying}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${isPlaying ? 'opacity-100' : 'opacity-0'}`}
        >
          {sources.map((source) => (
            <source key={source.src} src={source.src} type={source.type} />
          ))}
        </video>
      )}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(17,24,39,.15)_0%,rgba(17,24,39,.25)_40%,rgba(17,24,39,.78)_100%)] lg:bg-[linear-gradient(to_right,rgba(17,24,39,.72)_0%,rgba(17,24,39,.35)_55%,rgba(17,24,39,.05)_100%)]"
      />

      <div className="relative mx-auto flex h-full max-w-screen-xl items-end px-6 pb-8 lg:px-12 lg:pb-16">
        <div className="w-full lg:max-w-[720px]">
          <h1 className="sr-only">
            Flugsportverein Otto Lilienthal Bad Langensalza e.V. – Rundflüge, Segelflug und Ultraleichtflug am Flugplatz EDEB
          </h1>
          <figure>
            <blockquote
              data-quote-index={quoteIndex}
              className="mb-3.5 font-serif text-[27px] font-medium italic leading-[1.28] [text-shadow:0_2px_12px_rgba(0,0,0,.35)] lg:mb-[18px] lg:text-[44px] lg:leading-[1.22] lg:[text-shadow:0_2px_16px_rgba(0,0,0,.35)]"
            >
              „{quote.text}“
            </blockquote>
            <figcaption className="mb-7 text-sm font-medium tracking-[0.04em] text-blue-100 lg:mb-8 lg:text-base">
              — {quote.author}
            </figcaption>
          </figure>

          {/* Mobil führt "Mehr erfahren" zu den Rundflügen, am Desktop zu "Über uns" (wie im Design) */}
          <a href="#rundflug" className={`${ctaBase} flex min-h-[52px] justify-between px-[18px] text-base lg:hidden`}>
            Mehr erfahren <ArrowDownIcon />
          </a>
          <a href="#ueber-uns" className={`${ctaBase} hidden min-h-[52px] gap-3.5 px-[22px] text-base lg:inline-flex`}>
            Mehr erfahren <ArrowDownIcon />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Hero;
